<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue'
import { bbox, bboxPolygon, geometryCollection, intersect, area } from '@turf/turf'
import { MapSource, MapLayer } from '@/components/map'
import { Eye } from 'lucide-vue-next'
import { Spinner } from '@/components/ui/spinner'

const props = defineProps({
  sceneFeature: {
    type: Object,
    required: true
  },
  userGeometry: {
    type: Object,
    required: true
  },
  map: {
    type: Object,
    required: true
  },
  imageUrl: {
    type: String,
    required: true
  }
})

const corners = computed(() => {
  const [minLng, minLat, maxLng, maxLat] = bbox(props.sceneFeature)
  const width = maxLng - minLng
  const height = maxLat - minLat
  const size = Math.max(width, height)
  const centerLng = (minLng + maxLng) / 2
  const centerLat = (minLat + maxLat) / 2
  const half = size / 2
  return [
    [centerLng - half, centerLat + half],
    [centerLng + half, centerLat + half],
    [centerLng + half, centerLat - half],
    [centerLng - half, centerLat - half]
  ]
})

function latLonToCanvasXY(lng, lat, minLng, maxLng, minLat, maxLat, canvasSize) {
  const w = maxLng - minLng
  const h = maxLat - minLat
  const x = ((lng - minLng) / w) * canvasSize
  const y = ((maxLat - lat) / h) * canvasSize
  return [x, y]
}

const clippedImageUrl = ref(null)
const coveragePercentage = ref(0)
const clippedCanvas = ref(null)
const canvasOutput = ref(null)
const isPreview = ref(false)

function renderCanvasPreviewInUrl() {
  const [bboxMinLng, bboxMinLat, bboxMaxLng, bboxMaxLat] = bbox(props.sceneFeature)
  const w = bboxMaxLng - bboxMinLng
  const h = bboxMaxLat - bboxMinLat
  const size = Math.max(w, h)
  const centerLng = (bboxMinLng + bboxMaxLng) / 2
  const centerLat = (bboxMinLat + bboxMaxLat) / 2
  const [minLng, minLat, maxLng, maxLat] = [
    centerLng - size / 2,
    centerLat - size / 2,
    centerLng + size / 2,
    centerLat + size / 2
  ]

  const boundingBoxPoly = bboxPolygon([minLng, minLat, maxLng, maxLat])
  const intersectionPoly = intersect(
    geometryCollection([boundingBoxPoly.geometry, props.userGeometry])
  )
  if (intersectionPoly) {
    const intersectionArea = area(intersectionPoly)
    const boundingBoxArea = area(boundingBoxPoly)
    coveragePercentage.value = (intersectionArea / boundingBoxArea) * 100
  } else {
    coveragePercentage.value = 0
  }

  const canvasSize = 4096
  const canvas = document.createElement('canvas')
  canvas.width = canvasSize
  canvas.height = canvasSize
  const ctx = canvas.getContext('2d')
  const outerRing = props.userGeometry.coordinates[0]

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.filter = 'blur(1px)'

  ctx.beginPath()
  if (outerRing && outerRing.length > 0) {
    const [lng0, lat0] = outerRing[0]
    const [x0, y0] = latLonToCanvasXY(lng0, lat0, minLng, maxLng, minLat, maxLat, canvasSize)
    ctx.moveTo(x0, y0)
    for (let i = 1; i < outerRing.length; i++) {
      const [lngI, latI] = outerRing[i]
      const [xI, yI] = latLonToCanvasXY(lngI, latI, minLng, maxLng, minLat, maxLat, canvasSize)
      ctx.lineTo(xI, yI)
    }
    ctx.closePath()
  }
  ctx.clip()

  const url = new URL(props.imageUrl)
  url.searchParams.set('size', '2048')
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = url.href

  return new Promise((resolve, reject) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasSize, canvasSize)
      clippedImageUrl.value = canvas.toDataURL('image/png')

      const userOuterRing = props.userGeometry.coordinates[0] || []
      const xCoords = []
      const yCoords = []
      for (const [lng, lat] of userOuterRing) {
        const [x, y] = latLonToCanvasXY(lng, lat, minLng, maxLng, minLat, maxLat, canvasSize)
        xCoords.push(x)
        yCoords.push(y)
      }
      const minX = Math.floor(Math.min(...xCoords))
      const maxX = Math.ceil(Math.max(...xCoords))
      const minY = Math.floor(Math.min(...yCoords))
      const maxY = Math.ceil(Math.max(...yCoords))
      const clippedW = maxX - minX
      const clippedH = maxY - minY
      const croppedCanvas = document.createElement('canvas')
      croppedCanvas.width = clippedW
      croppedCanvas.height = clippedH
      const croppedCtx = croppedCanvas.getContext('2d')
      const clippedData = ctx.getImageData(minX, minY, clippedW, clippedH)
      croppedCtx.putImageData(clippedData, 0, 0)
      clippedCanvas.value = croppedCanvas
      resolve()
    }
    img.onerror = reject
  })
}

function renderMapPreview() {
  if (!isPreview.value) return
}

onMounted(async () => {
  await renderCanvasPreviewInUrl()
  await nextTick()
  if (canvasOutput.value && clippedCanvas.value) {
    canvasOutput.value.width = clippedCanvas.value.width
    canvasOutput.value.height = clippedCanvas.value.height
    const finalCtx = canvasOutput.value.getContext('2d')
    finalCtx.drawImage(clippedCanvas.value, 0, 0)
  }
})

watch(isPreview, (val) => {
  if (val) renderMapPreview()
})
</script>

<template>
  <MapSource
    v-if="clippedImageUrl && isPreview"
    :map="map"
    type="image"
    :id="`clipped-image-source-${sceneFeature.id}`"
    :url="clippedImageUrl"
    :coordinates="corners"
  >
    <MapLayer
      :map="map"
      :id="`clipped-image-layer-${sceneFeature.id}`"
      type="raster"
      :source="`clipped-image-source-${sceneFeature.id}`"
      :options="{
        paint: {
          'raster-opacity': 1,
          'raster-fade-duration': 0,
          'raster-resampling': 'nearest'
        }
      }"
    />
  </MapSource>
  <button
    type="button"
    class="min-w-32 min-h-32 w-32 h-32 border relative rounded cursor-pointer"
    :class="{'border-primary': isPreview}"
    @click="isPreview = !isPreview"
  >
    <div class="absolute top-0.5 left-0.5 flex items-center space-x-1 bg-gray-300 rounded p-1">
      <Eye class="w-4.5 h-4.5" />
    </div>
    <canvas
      v-if="clippedCanvas"
      ref="canvasOutput"
      class="w-full h-full object-cover rounded bg-gray-100 p-1.5"
    />
    <div v-else class="w-full h-full object-cover rounded bg-gray-100 flex items-center justify-center">
      <Spinner class="w-6 h-6" />
    </div>
  </button>
</template>
